import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";
import {
  BulkChatsShareCreate,
  BulkChatsShareResult,
  BulkSetSharesCreate,
  BulkSetSharesResult,
  ChatAccessInfo,
  ChatShareBulkCreate,
  ChatShareCreate,
  ChatShareResponse,
  ChatShareUpdate,
  CompanyUserInfo,
} from "../types/chat-shares";

export default class APIChatSharesSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  /**
   * Share a chat with a single user
   */
  shareChat = async (body: ChatShareCreate): Promise<ChatShareResponse> => {
    let res = await this._fetch({
      url: `/chat-shares/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Share a chat with multiple users
   */
  shareChatBulk = async (
    body: ChatShareBulkCreate,
  ): Promise<ChatShareResponse[]> => {
    let res = await this._fetch({
      url: `/chat-shares/bulk`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Share multiple chats with multiple users
   */
  shareBulkChats = async (
    body: BulkChatsShareCreate,
  ): Promise<BulkChatsShareResult[]> => {
    let res = await this._fetch({
      url: `/chat-shares/bulk-chats`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get all shares for a specific chat (owner only)
   */
  getChatShares = async (
    chatType: "whatsapp" | "sms",
    chatId: UUID,
  ): Promise<ChatShareResponse[]> => {
    let res = await this._fetch({
      url: `/chat-shares/chat/${chatType}/${chatId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get all chats shared with the current user
   */
  getSharedWithMe = async (
    chatType?: "whatsapp" | "sms",
  ): Promise<ChatShareResponse[]> => {
    const params = chatType ? `?chat_type=${chatType}` : "";
    let res = await this._fetch({
      url: `/chat-shares/shared-with-me${params}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Update permissions for a chat share
   */
  updateSharePermissions = async (
    shareId: UUID,
    body: ChatShareUpdate,
  ): Promise<ChatShareResponse> => {
    let res = await this._fetch({
      url: `/chat-shares/${shareId}`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Revoke a chat share
   */
  revokeShare = async (shareId: UUID): Promise<void> => {
    await this._fetch({
      url: `/chat-shares/${shareId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  /**
   * Check if current user has access to a chat
   */
  checkChatAccess = async (
    chatType: "whatsapp" | "sms",
    chatId: UUID,
  ): Promise<ChatAccessInfo> => {
    let res = await this._fetch({
      url: `/chat-shares/access/${chatType}/${chatId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get all users in the company for sharing
   */
  getCompanyUsers = async (): Promise<CompanyUserInfo[]> => {
    let res = await this._fetch({
      url: `/chat-shares/company-users`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Set shares on multiple chats (replaces existing shares)
   * This is useful for bulk operations where all selected chats should have
   * identical sharing settings.
   */
  bulkSetShares = async (
    body: BulkSetSharesCreate,
  ): Promise<BulkSetSharesResult[]> => {
    let res = await this._fetch({
      url: `/chat-shares/bulk-set-shares`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
